import { vaultFailure, isStorageFailure, reportVaultFailure } from './storageHealth';
import Dexie, { cmp, type Table } from 'dexie';
import { opaqueKey, seal, unseal, serialize, type SealedValue } from './vaultCrypto';

interface RecordEnvelope { id: string; collection: string; box: SealedValue }
type Key = string | number;
type Row = Record<string, unknown>;
function validKey(value: unknown): value is Key { return typeof value === 'string' || typeof value === 'number' && Number.isFinite(value); }
const databases = new Set<EncryptedDatabase>();
const backing = new Dexie('fusics-local-vault-v1');
backing.version(1).stores({ records: 'id,collection', meta: 'key' });

function indexValue(row: object, index: string): unknown {
  if (index.startsWith('[')) return index.slice(1, -1).split('+').map(key => (row as Row)[key]);
  return (row as Row)[index];
}
function compare(a: unknown, b: unknown): number {
  return cmp(a, b);
}

/** Queries operate on decrypted records in memory; no personal indexes reach disk. */
export class EncryptedCollection<T extends object, K extends Key = string> {
  private reversed = false;
  private maximum = Infinity;
  constructor(private table: EncryptedTable<T, K>, private predicate: (row: T) => boolean = () => true, private order?: string) {}
  and(predicate: (row: T) => boolean) { const previous = this.predicate; this.predicate = row => previous(row) && predicate(row); return this; }
  filter(predicate: (row: T) => boolean) { return this.and(predicate); }
  reverse() { this.reversed = !this.reversed; return this; }
  limit(maximum: number) { this.maximum = maximum; return this; }
  async toArray(): Promise<T[]> {
    const rows = (await this.table.readAll()).filter(this.predicate);
    if (this.order) rows.sort((a, b) => compare(indexValue(a, this.order!), indexValue(b, this.order!)));
    if (this.reversed) rows.reverse();
    return rows.slice(0, this.maximum);
  }
  async sortBy(index: string) { return (await this.toArray()).sort((a, b) => compare(indexValue(a, index), indexValue(b, index))); }
  async first() { return (await this.limit(1).toArray())[0]; }
  async count() { return (await this.toArray()).length; }
  async delete() {
    return this.table.database.write(async () => {
      const rows = await this.toArray();
      await this.table.bulkDelete(rows.map(row => (row as Row)[this.table.primaryKey] as K));
      return rows.length;
    });
  }
}

export class EncryptedTable<T extends object, K extends Key = string> {
  private creating?: (key: unknown, row: T, transaction: unknown) => unknown;
  private updating?: (changes: Partial<T>, key: K, row: T, transaction: unknown) => unknown;
  constructor(readonly database: EncryptedDatabase, readonly name: string, readonly primaryKey: string, private autoIncrement = false) {}
  private context() { return `${this.database.name}:${this.name}`; }
  private async id(key: K) { return `${this.name}:${await opaqueKey(this.context(), key)}`; }
  hook(event: 'creating' | 'updating', callback: (...args: any[]) => unknown) {
    if (event === 'creating') this.creating = callback;
    else this.updating = callback;
  }
  async readAll(): Promise<T[]> {
    await this.database.ready();
    return this.database.read(async () => {
      const rows = await this.database.records.where('collection').equals(this.context()).toArray();
      return Dexie.waitFor(Promise.all(rows.map(row => unseal<T>(row.box, `${this.context()}:${row.id}`))));
    });
  }
  toArray() { return this.readAll(); }
  count() { return new EncryptedCollection(this).count(); }
  orderBy(index: string) { return new EncryptedCollection(this, undefined, index); }
  filter(predicate: (row: T) => boolean) { return new EncryptedCollection(this, predicate); }
  where(index: string) {
    const collection = (predicate: (value: unknown) => boolean) => new EncryptedCollection(this, row => predicate(indexValue(row, index)));
    return {
      equals: (value: unknown) => collection(item => compare(item, value) === 0),
      anyOf: (values: unknown[]) => collection(item => values.some(value => compare(item, value) === 0)),
      above: (value: unknown) => collection(item => compare(item, value) > 0),
      below: (value: unknown) => collection(item => compare(item, value) < 0),
      between: (lower: unknown, upper: unknown, includeLower = true, includeUpper = false) => collection(item =>
        (includeLower ? compare(item, lower) >= 0 : compare(item, lower) > 0)
        && (includeUpper ? compare(item, upper) <= 0 : compare(item, upper) < 0)),
    };
  }
  async get(key: K): Promise<T | undefined> {
    await this.database.ready();
    return this.database.read(async () => {
      const id = await Dexie.waitFor(this.id(key));
      const row = await this.database.records.get(id);
      return row ? Dexie.waitFor(unseal<T>(row.box, `${this.context()}:${id}`)) : undefined;
    });
  }
  private async store(input: T, addOnly: boolean): Promise<K> {
    await this.database.ready();
    return this.database.write(async () => {
      const row = structuredClone(input);
      let key = (row as Row)[this.primaryKey] as K | undefined;
      if (key === undefined && this.autoIncrement) {
        key = (Math.max(0, ...(await this.readAll()).map(item => Number((item as Row)[this.primaryKey]))) + 1) as K;
        (row as Row)[this.primaryKey] = key;
      }
      if (!validKey(key)) throw new Error('Ungültiger lokaler Datensatzschlüssel.');
      const id = await Dexie.waitFor(this.id(key));
      const previous = await this.database.records.get(id);
      if (addOnly && previous) throw new Dexie.ConstraintError('Dieser lokale Datensatz existiert bereits.');
      if (!previous && this.creating) await this.creating(key, row, Dexie.currentTransaction);
      const box = await Dexie.waitFor(seal(row, `${this.context()}:${id}`));
      await this.database.records.put({ id, collection: this.context(), box });
      return key;
    });
  }
  put(row: T) { return this.store(row, false); }
  add(row: T) { return this.store(row, true); }
  async update(key: K, changes: Partial<T>): Promise<number> {
    return this.database.write(async () => {
      const row = await this.get(key);
      if (!row) return 0;
      if (this.updating) await this.updating(changes, key, row, Dexie.currentTransaction);
      await this.put({ ...row, ...changes });
      return 1;
    });
  }
  async bulkPut(rows: T[]) { return this.database.write(async () => { for (const row of rows) await this.put(row); }); }
  async bulkUpdate(rows: { key: K; changes: Partial<T> }[]) { return this.database.write(async () => { for (const row of rows) await this.update(row.key, row.changes); }); }
  async bulkDelete(keys: K[]) { return this.database.write(async () => { for (const key of keys) await this.delete(key); }); }
  async delete(key: K) { return this.database.write(async () => { const id = await Dexie.waitFor(this.id(key)); await this.database.records.delete(id); }); }
  async clear() { await this.database.ready(); return this.database.write(() => this.database.records.where('collection').equals(this.context()).delete()); }
}

export class EncryptedDatabase {
  readonly backing: Dexie;
  readonly records: Table<RecordEnvelope, string>;
  readonly tables: EncryptedTable<any, any>[] = [];
  private migration?: Promise<void>;
  constructor(readonly name: string, schema: Record<string, string>) {
    this.backing = backing;
    this.records = this.backing.table('records');
    for (const [table, key] of Object.entries(schema)) {
      const instance = new EncryptedTable(this, table, key.replace('++', ''), key.startsWith('++'));
      this.tables.push(instance);
      Object.assign(this, { [table]: instance });
    }
    databases.add(this);
  }
  async ready() {
    this.migration ??= this.migrate();
    await this.migration;
  }
  private async migrate() {
    const locks = globalThis.navigator?.locks;
    if (locks) return locks.request(`fusics:migrate:${this.name}`, () => this.migrateLocked());
    return this.migrateLocked();
  }
  private async migrateLocked() {
    await this.backing.open();
    let legacy = await new Promise<IDBDatabase | undefined>((resolve, reject) => {
      const request = indexedDB.open(this.name);
      let absent = false;
      request.onupgradeneeded = () => { absent = true; request.transaction!.abort(); };
      request.onerror = () => absent ? resolve(undefined) : reject(request.error);
      request.onsuccess = () => resolve(request.result);
      request.onblocked = () => reject(new Error('Bitte schließe andere FUSICS-Tabs und versuche es erneut.'));
    });
    if (!legacy) return;
    // A version change drains old transactions and closes legacy Dexie clients
    // before taking the snapshot. Otherwise an older tab could write after
    // getAll() and lose that update when its plaintext database is deleted.
    const nextVersion = legacy.version + 1;
    legacy.close();
    legacy = await new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(this.name, nextVersion);
      let blocked = false;
      request.onsuccess = () => { if (blocked) request.result.close(); else resolve(request.result); };
      request.onerror = () => reject(request.error);
      request.onblocked = () => { blocked = true; reject(new Error('Schließe ältere FUSICS-Tabs und starte FUSICS neu, damit dein Lernstand vollständig übernommen werden kann.')); };
    });
    legacy.onversionchange = () => legacy?.close();
    try {
      {
        const entries = await Promise.all(Array.from(legacy.objectStoreNames).map(async name => {
          const rows = await new Promise<object[]>((resolve, reject) => {
            const transaction = legacy!.transaction(name, 'readonly');
            const request = transaction.objectStore(name).getAll();
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
          });
          if (rows.length && !this.tables.some(table => table.name === name)) throw new Error('Ein alter Lernspeicher muss vor der Umstellung geprüft werden.');
          return { name, rows };
        }));
        // Do not call the public facade here: ready() is currently migrating.
        await this.backing.transaction('rw', this.records, this.backing.table('meta'), async () => {
          for (const entry of entries) {
            const table = this.tables.find(table => table.name === entry.name);
            if (!table) continue;
            for (const row of entry.rows) {
              if (!row || !validKey((row as Row)[table.primaryKey])) throw new Error('Ein alter Datensatz muss vor der Umstellung geprüft werden.');
              const context = `${this.name}:${entry.name}`;
              const id = `${entry.name}:${await Dexie.waitFor(opaqueKey(context, (row as Row)[table.primaryKey]))}`;
              const box = await Dexie.waitFor(seal(row, `${context}:${id}`));
              await this.records.put({ id, collection: context, box });
              const saved = await this.records.get(id);
              const restored = await Dexie.waitFor(unseal(saved!.box, `${context}:${id}`));
              if (serialize(restored) !== serialize(row)) throw new Error('Die Übernahme des Lernstands konnte nicht geprüft werden.');
            }
          }
          await this.backing.table('meta').put({ key: `${this.name}:legacy-migrated` });
        });
      }
    } finally { legacy.close(); }
    await new Promise<void>((resolve, reject) => {
      const request = indexedDB.deleteDatabase(this.name);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(new Error('Schließe ältere FUSICS-Tabs, damit die Speicherumstellung abgeschlossen werden kann.'));
    });
  }
  async read<T>(callback: () => Promise<T>): Promise<T> {
    await this.ready();
    try { return await this.backing.transaction('r', this.records, callback); }
    catch (error) { if (isStorageFailure(error)) reportVaultFailure(error); throw error; }
  }
  async write<T>(callback: () => Promise<T>): Promise<T> {
    await this.ready();
    if (vaultFailure()) throw vaultFailure();
    try { return await this.backing.transaction('rw', this.records, callback); }
    catch (error) { if (isStorageFailure(error)) reportVaultFailure(error); throw error; }
  }
  async transaction<T>(_mode: string, ...args: unknown[]): Promise<T> {
    const callback = args[args.length - 1] as () => Promise<T>;
    return this.write(callback);
  }
}

export async function prepareEncryptedDatabases() {
  await Promise.all([...databases].map(database => database.ready()));
  // Authenticate existing records before mounting UI; corrupt storage must not
  // be mistaken for a new pupil with an empty progress record.
  await Promise.all([...databases].flatMap(database => database.tables.map(table => table.toArray())));
}
export async function exportEncryptedDatabaseContents() {
  await prepareEncryptedDatabases();
  return backing.transaction('r', backing.table('records'), async () => Object.fromEntries(
    await Promise.all([...databases].map(async database => [database.name,
      Object.fromEntries(await Promise.all(database.tables.map(async table => [table.name, await table.toArray()]))),
    ])),
  ));
}
export async function importEncryptedDatabaseContents(contents: Record<string, Record<string, object[]>>) {
  await prepareEncryptedDatabases();
  for (const [name, tables] of Object.entries(contents)) {
    const database = [...databases].find(database => database.name === name);
    if (!database || !tables || typeof tables !== 'object' || Array.isArray(tables)) throw new Error('Unbekannter Speicher in der Sicherung.');
    for (const [name, rows] of Object.entries(tables)) {
      const table = database.tables.find(table => table.name === name);
      if (!table || !Array.isArray(rows) || rows.some(row => !row || typeof row !== 'object' || Array.isArray(row)
        || !validKey((row as Row)[table.primaryKey]))) throw new Error('Ungültige Datensätze in der Sicherung.');
    }
  }
  await backing.transaction('rw', backing.table('records'), async () => {
    for (const database of databases) {
      if (!contents[database.name]) continue;
      for (const table of database.tables) {
        if (contents[database.name][table.name]) await table.bulkPut(contents[database.name][table.name]);
      }
    }
  });
}

/** One transaction removes every registered personal record and migration marker. */
export async function clearEncryptedDatabaseContents(retainedBrowserState: Record<string, string>) {
  await prepareEncryptedDatabases();
  const browser = [...databases].find(database => database.name === 'fusics-browser-state');
  await backing.transaction('rw', backing.table('records'), backing.table('meta'), async () => {
    await backing.table('records').clear();
    await backing.table('meta').clear();
    if (browser) await browser.tables[0].bulkPut(Object.entries(retainedBrowserState).map(([key, value]) => ({ key, value })));
  });
}
